// The block standing in for the track that is playing.
//
// Segmentation runs on a timer (worker.service PASS_INTERVAL_SECONDS = 120), so a track that
// started thirty seconds ago exists nowhere in the segments table and cannot come back from
// /api/timeline at all. The title, though, is known within a second: /api/nowplaying reports it
// straight off the ICY log and the app-wide realtime transport delivers it. This turns
// that title into something the canvas can draw, so a new track appears when it starts playing
// rather than up to two and a half minutes later.
//
// Pure, and separate from the drawing code, so the rules about when a ghost is and is not
// warranted can be tested without a canvas.

import { splitRawTitle } from "../api/types";
import type { NowPlaying, Segment, TimelineBlock, TimelineWindow } from "../api/types";

/**
 * What the title log says, plus the row the server says is on air.
 *
 * The row is narrowed to the three fields recognising it takes, for the same reason the rest of
 * the arguments are `Pick`s: this module is pure, and asking a question about three fields
 * should not oblige its tests to build a whole segment.
 */
type Playing = Pick<NowPlaying, "raw_title" | "at" | "is_live"> & {
  segment: Pick<Segment, "kind" | "start" | "raw_title"> | null;
};

/**
 * A provisional track block covering the track now playing, or null when one is not warranted.
 *
 * How far back it reaches depends on how far the worker has got. Once a pass has written the row
 * for this same title, that row's start is where the track began, and this block takes it over
 * for as long as it plays -- draw.ts leaves a row the live block covers undrawn. The segmenter
 * ends the running track at the live edge of the pass that wrote it, so the alternative is one
 * track drawn as two: a settled block for the minute already recorded, and a ghost naming the
 * same track for the rest of it.
 *
 * Before that pass the title log's own instant is the start, clamped to the last track the
 * worker wrote: a title that has not changed for forty minutes would otherwise draw a
 * forty-minute ghost back over tracks that are already on the timeline.
 *
 * Suppressed in four cases, each for its own reason. A show on air is not this lane's business
 * at all -- see below. A lane the server bucketed is drawn as tick marks standing for several
 * tracks each, where one speculative tick says nothing. A title logged in the future is a
 * clock-skew artefact rather than a track. And a lane whose newest track already reaches the
 * present has no hole to fill -- unless that newest track is this one, which is not a hole but
 * the block itself.
 *
 * Note what is deliberately *not* a reason: a track carrying `on_air`. The server derives that
 * flag against the title log (core.onair), so it is the answer to "is this row the one playing",
 * which is a different question from "does the lane already hold a row for it" -- and in the case
 * this block exists for, the seconds and minutes after a title change, there is no such row to
 * carry any flag at all. The title log is the authority on what is playing now, and this is the
 * only thing that reads it.
 */
export function ghostTrack(
  nowPlaying: Playing | null,
  data: Pick<TimelineWindow, "tracks" | "bucket_seconds"> | null,
  nowMs: number,
): TimelineBlock | null {
  if (!nowPlaying?.raw_title || !nowPlaying.at) return null;

  // A show on air leaves no hole here. The show lane draws that block itself and both lanes share
  // one band (theme.bands), so a ghost over a show does not sit beside it: it lands on top, and
  // the block reads with two titles at once -- the parsed one and the raw one this would carry.
  //
  // Two signals because they arrive at different times. `is_live` is the station's own marker
  // read straight off the title, true from the first second and before any row exists, which is
  // the window this block was written for. The row is the settled answer, and catches a show the
  // markers do not name -- one the schedule backstop wrote, say.
  if (nowPlaying.is_live) return null;
  if (nowPlaying.segment && nowPlaying.segment.kind !== "track") return null;

  const tracks = data?.tracks ?? [];
  if (data && data.bucket_seconds > 0) return null;
  let newestEnd = 0;
  // The stamp as well as the number, for the reason below: the newest row's end is the same
  // instant as this track's start, and it is the clamp's answer.
  let newestEndAt: string | null = null;
  for (const track of tracks) {
    if (track.id === null) return null; // bucketed after all, whatever bucket_seconds said
    const end = Date.parse(track.end);
    if (end > newestEnd) {
      newestEnd = end;
      newestEndAt = track.end;
    }
  }

  // The stamp, not a number parsed out of it: a click on this block opens playout at the
  // instant named here, and `Date.parse` truncates to milliseconds while a span is stored to
  // the microsecond -- see player/useAudioSession's `startAt` for what a rounded-down instant
  // costs. Only the clamp needs a number.
  const writtenAt = writtenStart(nowPlaying);
  const startedAt =
    writtenAt ??
    (Date.parse(nowPlaying.at) >= newestEnd ? nowPlaying.at : (newestEndAt ?? nowPlaying.at));
  const startMs = Date.parse(startedAt);
  if (!Number.isFinite(startMs) || startMs >= nowMs) return null;

  return {
    // Null, like a bucket, and for a related reason: there is nothing here to open that is
    // finished. Clicking it falls through to playing the archive from that instant, which keeps
    // rolling into the live edge -- where the row, whose playlist ends where the last pass
    // ended, would stop a minute in.
    id: null,
    kind: "track",
    start: startedAt,
    // The end is nominal. `on_air` is what makes it track the clock, through `liveEndMs`.
    end: new Date(nowMs).toISOString(),
    // Split the way the worker will split it (titles._SEPARATOR, parse_track_title), so the
    // block stacks artist over title like every settled track in the lane instead of printing
    // the raw string as one row and rearranging itself when the pass lands. An unsplittable
    // title stays whole as the title, not the artist -- same rule as the worker's.
    ...splitRawTitle(nowPlaying.raw_title),
    djs: null,
    city: null,
    marker: null,
    state: null,
    starred: false,
    playable: true,
    edited: false,
    on_air: true,
    // Not a match: the fingerprinter has not run on this second of audio, and a ghost over a
    // show never exists (a live show returns null above).
    matched: false,
    count: 1,
    // A track never carries the listened tracker, and this one is not even a row yet.
    listened_pct: null,
  };
}

/**
 * Where the row the worker has already written for the title now playing begins, or null when
 * there is no such row.
 *
 * `segment` is the server's own answer to what is on air (api.events.now_playing), so the two
 * are matched on the raw title: that is the string both sides took from the title log, where the
 * parsed fields are a reading of it that an operator may since have corrected. The kind needs no
 * check: a non-track row on air has already stood the whole block down.
 *
 * Preferred over the title log's instant, which is not reliably where the track began: the
 * recorder writes the title it finds every time it reconnects, so a stream that dropped
 * mid-track leaves a second event for the same title, and the row is the only thing that still
 * knows where the track started.
 */
function writtenStart(nowPlaying: Playing): string | null {
  const segment = nowPlaying.segment;
  if (!segment) return null;
  if (segment.raw_title !== nowPlaying.raw_title) return null;
  return Number.isFinite(Date.parse(segment.start)) ? segment.start : null;
}
